/**
 * Detrazioni Fiscali - Bonus edilizi Italia
 * Calcola le detrazioni per ristrutturazioni, bonus mobili, ecobonus, etc.
 */

import type { Preventivo } from '../types';

export interface DetrazioneConfig {
  id: string;
  nome: string;
  descrizione: string;
  percentuale: number;
  massimoDetraibile: number;
  anni: number;
  categorie: string[]; // Categorie preventivo ammesse
  attivo: boolean;
}

export interface DetrazioneCalcolata {
  config: DetrazioneConfig;
  speseAmmesse: number;
  detrazioneTotale: number;
  detrazioneAnnua: number;
  preventivi: Preventivo[];
}

export interface DetrazioniSummary {
  totaleSpese: number;
  totaleDetrazioni: number;
  detrazioneAnnua: number;
  anni: number;
  detrazioni: DetrazioneCalcolata[];
}

// Configurazioni detrazioni 2024-2025
export const DETRAZIONI_DISPONIBILI: DetrazioneConfig[] = [
  {
    id: 'ristrutturazione',
    nome: 'Bonus Ristrutturazione',
    descrizione: 'Detrazione 50% per lavori di ristrutturazione edilizia',
    percentuale: 50,
    massimoDetraibile: 96000,
    anni: 10,
    categorie: ['ristrutturazione', 'edilizia', 'muratura', 'impianti', 'elettrico', 'idraulico'],
    attivo: true
  },
  {
    id: 'mobili',
    nome: 'Bonus Mobili',
    descrizione: 'Detrazione 50% per mobili e grandi elettrodomestici',
    percentuale: 50,
    massimoDetraibile: 8000, // 2024
    anni: 10,
    categorie: ['mobili', 'arredamento', 'elettrodomestici', 'cucina'],
    attivo: true
  },
  {
    id: 'ecobonus',
    nome: 'Ecobonus',
    descrizione: 'Detrazione 65% per riqualificazione energetica',
    percentuale: 65,
    massimoDetraibile: 100000,
    anni: 10,
    categorie: ['energia', 'isolamento', 'caldaia', 'infissi', 'pannelli', 'climatizzazione'],
    attivo: true
  },
  {
    id: 'verde',
    nome: 'Bonus Verde',
    descrizione: 'Detrazione 36% per sistemazione giardini e terrazzi',
    percentuale: 36,
    massimoDetraibile: 5000,
    anni: 10,
    categorie: ['giardino', 'verde', 'terrazzo', 'balcone'],
    attivo: true
  },
  {
    id: 'sismabonus',
    nome: 'Sismabonus',
    descrizione: 'Detrazione 50-85% per interventi antisismici',
    percentuale: 50,
    massimoDetraibile: 96000,
    anni: 5,
    categorie: ['sismica', 'strutturale', 'consolidamento'],
    attivo: true
  }
];

/**
 * Verifica se una categoria preventivo è ammessa per una detrazione
 */
function categoriaAmmessa(categoria: string, categorieAmmesse: string[]): boolean {
  const catLower = categoria.toLowerCase();
  return categorieAmmesse.some(c =>
    catLower.includes(c) || c.includes(catLower)
  );
}

/**
 * Calcola le detrazioni per i preventivi
 */
export function calcolaDetrazioni(
  preventivi: Preventivo[],
  estimateLevel: 'min' | 'med' | 'max',
  configsAttive?: DetrazioneConfig[]
): DetrazioniSummary {
  const configs = configsAttive || DETRAZIONI_DISPONIBILI.filter(c => c.attivo);

  const detrazioni: DetrazioneCalcolata[] = [];
  let totaleSpese = 0;
  let totaleDetrazioni = 0;

  // Traccia i preventivi già assegnati per evitare doppi conteggi
  const preventiviAssegnati = new Set<string>();

  // Calcola per ogni detrazione
  configs.forEach(config => {
    // Trova preventivi ammessi (non ancora assegnati)
    const preventiviAmmessi = preventivi.filter(p =>
      !preventiviAssegnati.has(p.id) &&
      categoriaAmmessa(p.categoria, config.categorie)
    );

    // Calcola spese
    let spese = 0;
    preventiviAmmessi.forEach(p => {
      let costo: number;
      switch (estimateLevel) {
        case 'min': costo = parseFloat(p.costoMin) || 0; break;
        case 'max': costo = parseFloat(p.costoMax) || 0; break;
        default: costo = parseFloat(p.costoMed) || 0;
      }
      spese += costo;
      preventiviAssegnati.add(p.id);
    });

    // Applica massimale
    const speseAmmesse = Math.min(spese, config.massimoDetraibile);

    // Calcola detrazione
    const detrazioneTotale = speseAmmesse * (config.percentuale / 100);
    const detrazioneAnnua = detrazioneTotale / config.anni;

    if (speseAmmesse > 0) {
      totaleSpese += speseAmmesse;
      totaleDetrazioni += detrazioneTotale;

      detrazioni.push({
        config,
        speseAmmesse,
        detrazioneTotale,
        detrazioneAnnua,
        preventivi: preventiviAmmessi
      });
    }
  });

  // Trova l'anno massimo per la detrazione media
  const anniMax = detrazioni.length > 0
    ? Math.max(...detrazioni.map(d => d.config.anni))
    : 10;

  const detrazioneAnnuaMedia = totaleDetrazioni / anniMax;

  return {
    totaleSpese,
    totaleDetrazioni,
    detrazioneAnnua: detrazioneAnnuaMedia,
    anni: anniMax,
    detrazioni
  };
}

/**
 * Formatta importo in valuta
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
}

/**
 * Suggerisce la categoria di detrazione per una voce preventivo
 */
export function suggerisciDetrazione(categoria: string): DetrazioneConfig | null {
  const catLower = categoria.toLowerCase();

  for (const config of DETRAZIONI_DISPONIBILI) {
    if (categoriaAmmessa(catLower, config.categorie)) {
      return config;
    }
  }

  return null;
}
